import React, { useEffect, useState } from "react";
import { Button } from "../components/Button.jsx";
import { usePlayer, useStage, useRound, useGame } from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import { useChatAutoScroll } from "../utils/useChatAutoScroll.js";

export function AISummary() {
  const player = usePlayer();
  const stage = useStage();
  const round = useRound();
  const game = useGame();
  const taskConfig = getTaskConfig(game.get("treatment") || {});
  const DetailsComponent = taskConfig.DetailsComponent;
  const detailsToggle = taskConfig.detailsToggle || {};
  const detailsTitle = taskConfig.detailsTitle || "Case details";

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [expectingAiResponse, setExpectingAiResponse] = useState(false);
  const [hasUserParticipated, setHasUserParticipated] = useState(false);
  const [showLoanCase, setShowLoanCase] = useState(true);
  const [forceScrollKey, setForceScrollKey] = useState(0);
  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    handleScroll,
  } = useChatAutoScroll({
    itemCount: messages.length,
    typingVisible: isAiTyping,
    forceScrollKey,
  });

  const collectMessages = () => {
    const stageTranscript = stage.get("messages") ?? [];
    const stageMessages = Array.isArray(stageTranscript?.messages)
      ? stageTranscript.messages
      : Array.isArray(stageTranscript)
      ? stageTranscript
      : [];

    const playerTranscript = player.stage.get("messages") ?? [];
    const playerMessages = Array.isArray(playerTranscript?.messages)
      ? playerTranscript.messages
      : Array.isArray(playerTranscript)
      ? playerTranscript
      : [];

    // Combine and de-duplicate by id (stage + player only, matching pre-lobby scoping)
    const combined = [...stageMessages, ...playerMessages];
    const seen = new Set();
    const deduped = combined.filter((m) => {
      const key = m?.id || `${m?.subjectId || "unknown"}-${m?.createdAt || 0}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const filtered = deduped
      .filter((m) => {
        if (!m) return false;
        const isMine = m.subjectId === player.id || m.meta?.privateFor === player.id;
        const isAiForMe = m.meta?.isAI && (m.meta?.privateFor === player.id || !m.meta?.privateFor);
        return isMine || isAiForMe;
      })
      .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));

    // If both a real seeded summary and a client fallback exist, prefer the real and drop fallback
    const hasRealSeed = filtered.some((m) => m?.meta?.summaryInitial && !m?.meta?.fallback);
    if (hasRealSeed) {
      return filtered.filter((m) => !(m?.meta?.summaryInitial && m?.meta?.fallback));
    }
    return filtered;
  };

  // Initial load
  useEffect(() => {
    setMessages(collectMessages());
  }, [stage?.id, player?.id]);

  // Listen for server-triggered updates (append-only, like pre-lobby)
  useEffect(() => {
    const trigger = stage.get("messageUpdateTrigger");
    const gameTrigger = game?.get("aiResponseTrigger");
    if (trigger || gameTrigger) {
      const incoming = collectMessages();
      const currentIds = new Set(messages.map((m) => m.id));
      const newOnes = incoming.filter((m) => m && !currentIds.has(m.id));
      if (newOnes.length > 0) {
        setMessages((prev) => [...prev, ...newOnes].sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0)));
        const hasAi = newOnes.some((m) => m?.meta?.isAI);
        if (hasAi) {
          setIsAiTyping(false);
          setExpectingAiResponse(false);
        }
      }
    }
  }, [stage?.get("messageUpdateTrigger"), game?.get("aiResponseTrigger"), messages]);

  // Append-only refresh on message changes (stage/player), mirroring pre-lobby diffing
  useEffect(() => {
    const incoming = collectMessages();
    const currentIds = new Set(messages.map((m) => m.id));
    const newOnes = incoming.filter((m) => m && !currentIds.has(m.id));
    if (newOnes.length > 0) {
      setMessages((prev) => [...prev, ...newOnes].sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0)));
      const hasAi = newOnes.some((m) => m?.meta?.isAI);
      if (hasAi) {
        setIsAiTyping(false);
        setExpectingAiResponse(false);
      }
    }
  }, [stage?.get("messages"), player?.stage?.get("messages")]);

  // Fallback: a short retry loop on mount to catch late-arriving seeded message
  useEffect(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const fresh = collectMessages();
      setMessages(fresh);
      const hasSeed = fresh.some((m) => m?.meta?.summaryInitial);
      if (hasSeed || attempts >= 6) {
        clearInterval(interval);
        setIsAiTyping(false);
        setExpectingAiResponse(false);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Client-side fallback: seed summary message if server seed hasn't arrived yet (mirrors pre-lobby behavior)
  useEffect(() => {
    const timeout = setTimeout(() => {
      const existing = collectMessages();
      const hasSeed = existing.some((m) => m?.meta?.summaryInitial);
      if (hasSeed) return;

      const perPlayer = stage.get("perPlayerSummaries")?.[player.id] || round?.get("perPlayerSummaries")?.[player.id];
      const summaryText =
        perPlayer ||
        round?.get("currentSummary") ||
        stage.get("aiSummary");
      if (!summaryText) return;

      const now = Date.now();
      const msg = {
        id: `ai-summary-fallback-${player.id}-${now}`,
        role: "assistant",
        text: summaryText,
        createdAt: now,
        subjectId: "llm-summary",
        subject: { id: "llm-summary", name: "Assistant" },
        meta: { isAI: true, privateFor: player.id, summaryInitial: true, stage: "aiSummary", fallback: true },
      };

      // stage messages
      const stageTranscript = stage.get("messages") || [];
      const stageArr = Array.isArray(stageTranscript?.messages)
        ? stageTranscript.messages
        : Array.isArray(stageTranscript)
        ? stageTranscript
        : [];
      const updatedStage = Array.isArray(stageTranscript?.messages)
        ? { ...stageTranscript, messages: [...stageArr, msg] }
        : [...stageArr, msg];
      stage.set("messages", updatedStage);

      // player messages
      const pTranscript = player.stage.get("messages") || [];
      const pArr = Array.isArray(pTranscript?.messages) ? pTranscript.messages : Array.isArray(pTranscript) ? pTranscript : [];
      const updatedPlayer = Array.isArray(pTranscript?.messages)
        ? { ...pTranscript, messages: [...pArr, msg] }
        : [...pArr, msg];
      player.stage.set("messages", updatedPlayer);

      // Notify listeners
      stage.set("messageUpdateTrigger", Date.now());

      setMessages(collectMessages());
    }, 600); // small delay to allow server seed; mirrors pre-lobby immediacy

    return () => clearTimeout(timeout);
  }, [stage?.id, player?.id, round?.id]);

  // Stop typing indicator when an AI message arrives
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.meta?.isAI) {
      setIsAiTyping(false);
      setExpectingAiResponse(false);
    }
    // Mark participation if the player has sent at least one message
    if (!hasUserParticipated) {
      const hasUserMsg = messages.some(
        (m) => m && !m?.meta?.isAI && (m.subjectId === player.id || m.meta?.privateFor === player.id)
      );
      if (hasUserMsg) {
        setHasUserParticipated(true);
      }
    }
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || expectingAiResponse) return;
    const ts = Date.now();
    const msg = {
      id: `summary-chat-${ts}`,
      role: "user",
      text,
      createdAt: ts,
      subjectId: player.id,
      subject: { id: player.id, name: player.get("nickname") || "You" },
      meta: { privateFor: player.id },
    };

    // stage messages
    const stageTranscript = stage.get("messages") || [];
    const stageArr = Array.isArray(stageTranscript?.messages)
      ? stageTranscript.messages
      : Array.isArray(stageTranscript)
      ? stageTranscript
      : [];
    const updatedStage = Array.isArray(stageTranscript?.messages)
      ? { ...stageTranscript, messages: [...stageArr, msg] }
      : [...stageArr, msg];
    stage.set("messages", updatedStage);

    // player messages
    const pTranscript = player.stage.get("messages") || [];
    const pArr = Array.isArray(pTranscript?.messages) ? pTranscript.messages : Array.isArray(pTranscript) ? pTranscript : [];
    const updatedPlayer = Array.isArray(pTranscript?.messages)
      ? { ...pTranscript, messages: [...pArr, msg] }
      : [...pArr, msg];
    player.stage.set("messages", updatedPlayer);

    // Immediate local echo (match pre-lobby responsiveness)
    setMessages((prev) => [...prev, msg]);

    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: "ai-summary-chat-input",
      text,
      phase: "in-game",
      stageId: stage?.id,
      stageName: stage?.get("name"),
      detectedAt: ts,
    });

    setInput("");
    setIsAiTyping(true);
    setExpectingAiResponse(true);
    setHasUserParticipated(true);
    setForceScrollKey((prev) => prev + 1);
  };

  function onFinish() {
    player.stage.set("submittedAt", Date.now());
    player.stage.set("submit", true);
  }

  return (
    <div className="h-full min-h-0 w-full max-w-5xl mx-auto p-6 flex flex-col">
      <h2 className="text-2xl font-bold mb-4 shrink-0">AI Debrief & Clarification</h2>
      <p className="text-sm text-gray-700 mb-6 shrink-0">
        Chat with the assistant to understand the group’s deliberation and confirm the summary captures the key points before you proceed.
        It may take 5–10 seconds for the assistant to respond.
      </p>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <div className="space-y-6">
          {/* Loan details above chat, matching pre-lobby layout */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{detailsTitle}</h3>
              <button
                type="button"
                className="text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
                onClick={() => setShowLoanCase((prev) => !prev)}
              >
                {showLoanCase ? (detailsToggle.hide || "🙈 Hide case") : (detailsToggle.show || "👁️ Show case")}
              </button>
            </div>
            {showLoanCase ? (
              <div className="max-h-72 overflow-y-auto pr-2">
                {DetailsComponent ? <DetailsComponent /> : null}
              </div>
            ) : (
              <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50">
                {detailsToggle.hiddenText || "Case hidden. Click \"Show case\" to view the details again."}
              </div>
            )}
          </div>

          {/* Chat Interface (aligned with pre-lobby styling) */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col min-h-[28rem]">
            <div
              ref={messagesContainerRef}
              className="flex-1 min-h-0 overflow-y-auto mb-4 border rounded-lg p-4 bg-gray-50"
              onScroll={handleScroll}
            >
              {messages.map((m) => {
                const isAI = m?.meta?.isAI;
                const isMe = m?.subjectId === player.id && !isAI;
                return (
                  <div key={m.id || m.createdAt} className={`mb-4 ${isAI ? "text-left" : "text-right"}`}>
                    <div
                      className={`inline-block max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                        isAI ? "bg-blue-100 text-blue-900" : "bg-green-100 text-green-900"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold">
                          {isAI ? "🤖 Assistant" : "👤 You"}
                        </span>
                        <span className="text-xs opacity-70">
                          {m.createdAt ? new Date(Number(m.createdAt)).toLocaleTimeString() : ""}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{m.text || m.content}</p>
                      {m?.meta?.summaryInitial && (
                        <div className="text-[10px] text-blue-700 mt-1">Group discussion summary</div>
                      )}
                    </div>
                  </div>
                );
              })}

              {isAiTyping && (
                <div className="text-left mb-4">
                  <div className="inline-block px-4 py-2 rounded-lg bg-blue-100 text-blue-900">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">🤖 Assistant</span>
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="flex gap-2 shrink-0">
              <input
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-empirica-500 focus:border-empirica-500"
                placeholder="Share your thoughts with the AI assistant..."
                data-integrity-field="ai-summary-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={expectingAiResponse}
              />
              <Button
                handleClick={handleSend}
                disabled={!input.trim() || expectingAiResponse}
                className="px-4 py-2 bg-empirica-500 hover:bg-empirica-600 disabled:opacity-50"
              >
                Send
              </Button>
            </div>
          </div>

          {/* AI Response Note */}
          {expectingAiResponse && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> If you do not receive an AI response within 20 seconds, please refresh the browser and resend your message.
              </p>
            </div>
          )}

          {/* Proceed CTA mirrors pre-lobby */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Discussion Complete?</h3>
            <p className="mb-4 text-gray-600">
              When you're satisfied with your discussion with the AI assistant, click below to continue.
            </p>

            <div className="flex justify-center">
              <Button
                className="px-8 py-4 bg-green-500 hover:bg-green-600 disabled:opacity-50"
                handleClick={onFinish}
                disabled={!hasUserParticipated}
              >
                Continue
              </Button>
            </div>
            {!hasUserParticipated && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Please send at least one message to the AI assistant first.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
